import { navigate } from "../router";
import { lazy, Suspense, useEffect, useState, type MouseEvent } from "react";
import { useLantern, useSession } from "../hooks";
import { RichText } from "../text";
import { Attachments } from "./Attachments";
import { taskPayloadSchema, type Message, type Shortcut } from "../sync/types";
import { Blocks } from "./Blocks";
import { PopMenu } from "./PopMenu";
import { TaskCard } from "./TaskCard";
import { Avatar, useDensity, useTip } from "./ui";
import { glyph, noteUsed } from "../emoji";
import { Emoji } from "./Emoji";
import { EmojiPicker } from "./EmojiPicker";
import { IconBack, IconBolt, IconBookmark, IconClock, IconCopy, IconEdit, IconLink, IconMore, IconPin, IconReaction, IconReply, IconShare, IconTask, IconTrash, IconUnread } from "./icons";
import { RemindPicker } from "./Reminders";
import { LinkPreviews, PagePreviews } from "./LinkPreviews";
import { ShareMessage } from "./ShareMessage";
import { permalink } from "../links";
import { list, locale, t, tn, tx } from "../i18n";

const quickReactions = ["+1", "eyes", "tada", "white_check_mark"];

const NewTaskSheet = lazy(() => import("./NewTaskSheet").then((m) => ({ default: m.NewTaskSheet })));

// MessageItem is one message. compact is a follow-up from the same author a
// moment after the last: no avatar or name, its time shown on hover.
export function MessageItem({ m, onThread, highlight, compact }: { m: Message; onThread?: () => void; highlight?: boolean; compact?: boolean }) {
  const session = useSession();
  const dense = useDensity() === "compact";
  const saved = useLantern().saved.has(m.id);
  const me = session.store.getSnapshot().me?.id;
  // An agent's owner and admins can look into how it came to post this.
  const canTrace =
    m.author.kind === "agent" && (session.store.getSnapshot().me?.role === "admin" || session.directory.get(m.author.id)?.owner_id === me);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text);
  const mine = m.author.id === me;
  // A reply sent to the channel too names, in the channel, what it replies to.
  const root = useLantern().channels.get(m.channel_id)?.messages.get(m.thread_root_id ?? "");
  const rootText = root ? root.text.replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => "@" + session.name(id)).slice(0, 80) : "";
  // Admins may delete anyone's message, as the server allows.
  const admin = useLantern().me?.role === "admin";
  // One's own message can be recalled for a while after sending it, and
  // anyone's by an admin; the server has the last word.
  const recallSeconds = useLantern().recallSeconds;
  const canRecall =
    !m.recalled_at && m.payload_schema !== taskPayloadSchema && m.class !== "ephemeral" && (admin || (mine && recallSeconds > 0 && Date.now() - Date.parse(m.created_at) < recallSeconds * 1000));
  const time = new Date(m.created_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  const fullTime = new Date(m.created_at).toLocaleString(locale, { dateStyle: "full", timeStyle: "short" });
  // A task's thread root shows as its card; the text is for other clients.
  const taskID = m.payload_schema === taskPayloadSchema ? (m.payload as { task_id?: unknown } | null)?.task_id : undefined;

  // The emoji picker, open beside the button that asked for it.
  const [picking, setPicking] = useState<DOMRect | null>(null);
  // The More actions menu, open by its button.
  const [more, setMore] = useState<DOMRect | null>(null);
  const pinnedBy = useLantern().pins.get(m.channel_id)?.get(m.id);
  const openPicker = (e: MouseEvent<HTMLElement>) => setPicking(picking ? null : e.currentTarget.getBoundingClientRect());

  async function react(emoji: string) {
    noteUsed(emoji);
    const has = m.reactions?.find((r) => r.emoji === emoji)?.principal_ids.includes(me ?? "");
    await session.api.react(m.channel_id, m.id, emoji, !has).catch(fail);
  }

  // Workflows people can start from this message.
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [menu, setMenu] = useState<DOMRect | null>(null);
  const [started, setStarted] = useState("");
  const [reminding, setReminding] = useState<DOMRect | null>(null);
  const [sharing, setSharing] = useState(false);
  // asking is the sheet for a new task in this message's thread.
  const [asking, setAsking] = useState(false);
  // Set once a recalled message's text is back in a message box.
  const [reedited, setReedited] = useState(false);
  // say shows a short note by the message's tools for a while.
  const say = (note: string) => {
    setStarted(note);
    setTimeout(() => setStarted(""), 4000);
  };
  // fail says why something done to the message did not happen.
  const fail = (err: unknown) => say(err instanceof Error ? err.message : String(err));
  useEffect(() => {
    if (m.class === "ephemeral") return;
    let live = true;
    void session.shortcuts(m.channel_id).then((list) => live && setShortcuts(list));
    return () => {
      live = false;
    };
  }, [session, m.channel_id, m.class]);

  async function runShortcut(s: Shortcut) {
    setMenu(null);
    try {
      await session.api.runShortcut(s.workflow_id, m.channel_id, m.id);
      setStarted(t("Started {name}", { name: s.label }));
    } catch (err) {
      setStarted(err instanceof Error ? err.message : String(err));
    }
    setTimeout(() => setStarted(""), 4000);
  }

  // The message box asks to edit one's last message (the Up key).
  useEffect(() => {
    if (!mine) return;
    const on = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== m.id) return;
      setDraft(m.text);
      setEditing(true);
      document.getElementById(`m-${m.id}`)?.scrollIntoView({ block: "nearest" });
    };
    addEventListener("lantern:edit", on);
    return () => removeEventListener("lantern:edit", on);
  }, [mine, m.id, m.text]);

  // readable is a message's text with mentions as names.
  const readable = (text: string) =>
    text.replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => "@" + session.name(id)).replace(/<!subteam\^[0-9a-f-]{36}\|(@[^>]+)>/g, "$1");
  // A link to this message: a reply's opens its thread.
  const copyLink = () => copy(permalink(location.origin, m), t("Link copied"));
  async function copy(text: string, said: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // No clipboard API over plain HTTP: copy through a selection.
      const area = document.createElement("textarea");
      area.value = text;
      document.body.append(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setStarted(said);
    setTimeout(() => setStarted(""), 2500);
  }

  async function save() {
    setEditing(false);
    // A change that did not save opens again, so nothing typed is lost.
    if (draft.trim() && draft !== m.text)
      await session.api.edit(m.channel_id, m.id, session.mentionsIn(draft)).catch((err) => {
        setEditing(true);
        fail(err);
      });
  }

  // A thread's root one cleared from one's history keeps its place, without
  // its content, so the thread's newer replies still open (ADR-0024).
  if (m.cleared)
    return (
      <article className="message recalled" id={`m-${m.id}`} tabIndex={-1}>
        <p className="recalled-note">{t("You cleared this message from your history.")}</p>
      </article>
    );

  // A recalled message keeps its place as a note of who recalled it; its
  // sender can put what it said back in the message box.
  if (m.recalled_at) {
    const by = m.recalled_by ?? m.author.id;
    const kept = mine && !reedited ? session.recalledText(m.id) : undefined;
    // Into the thread's box when it is open, else the channel's; a box
    // that takes the text says so by cancelling the event.
    const reedit = (text: string) => {
      const put = (thread: string | null) => !dispatchEvent(new CustomEvent("lantern:reedit", { cancelable: true, detail: { channel: m.channel_id, thread, text } }));
      if (put(m.thread_root_id ?? null) || (m.thread_root_id && put(null))) {
        session.forgetRecalled(m.id);
        setReedited(true);
      }
    };
    const note =
      by !== m.author.id
        ? t("{admin} recalled a message from {name}.", { admin: session.name(by), name: session.name(m.author.id) })
        : mine
          ? t("You recalled a message.")
          : t("{name} recalled a message.", { name: session.name(m.author.id) });
    return (
      <article className={`message recalled ${highlight ? "highlight" : ""}`} id={`m-${m.id}`} tabIndex={-1}>
        <p className="recalled-note">
          <time dateTime={m.recalled_at} title={new Date(m.recalled_at).toLocaleString(locale, { dateStyle: "full", timeStyle: "short" })}>
            {new Date(m.recalled_at).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}
          </time>{" "}
          {note}
          {kept && (
            <button
              onClick={() => reedit(readable(kept))}
            >
              {t("Re-edit")}
            </button>
          )}
        </p>
        {onThread && (m.reply_count ?? 0) > 0 && (
          <button className="replies" onClick={onThread}>
            <IconReply size={15} />
            {tn(m.reply_count!, "{n} reply", "{n} replies")}
          </button>
        )}
      </article>
    );
  }

  return (
    <article className={`message ${m.class} ${highlight ? "highlight" : ""} ${compact ? "compact" : ""} ${m.author.id === me && m.class !== "ephemeral" ? "mine" : ""}`} id={`m-${m.id}`} tabIndex={-1}>
      {(saved || pinnedBy) && (
        <div className="saved-note">
          {pinnedBy && (
            <span className="pinned-note">
              <IconPin size={12} /> {t("Pinned by {name}", { name: session.name(pinnedBy) })}
            </span>
          )}
          {saved && (
            <span>
              <IconBookmark size={12} filled /> {t("Saved for later")}
            </span>
          )}
        </div>
      )}
      <div className="gutter">
        {compact ? (
          <time className="hover-time" dateTime={m.created_at} title={fullTime}>
            {time.replace(/\s?[AP]M$/i, "")}
          </time>
        ) : (
          <button className="avatar-button" data-profile={m.author.id} aria-label={t("{name}'s profile", { name: session.name(m.author.id) })}>
            <Avatar id={m.author.id} name={session.name(m.author.id)} size={dense ? 28 : 36} />
          </button>
        )}
      </div>
      <div className="message-body">
        {!compact && (
          <header>
            <button className="author" data-profile={m.author.id} title={session.name(m.author.id)}>
              {session.name(m.author.id)}
            </button>
            {session.statusOf(m.author.id)?.emoji && (
              <span className="status-emoji" title={session.statusOf(m.author.id)!.text}>
                {session.statusOf(m.author.id)!.emoji}
              </span>
            )}
            {m.author.kind === "agent" && <span className="agent-badge">{t("agent")}</span>}
            {m.on_behalf_of && (
              <span className="on-behalf">
                {tx("for {name}", {
                  name: (
                    <span className="profile-link" data-profile={m.on_behalf_of} role="button" tabIndex={0}>
                      {session.name(m.on_behalf_of)}
                    </span>
                  ),
                })}
              </span>
            )}
            <time dateTime={m.created_at} title={fullTime}>
              {time}
            </time>
            {m.trace_id && canTrace && (
              <button className="trace-link" onClick={() => navigate(`/traces/${m.trace_id}`)} title={t("What the agent did to post this")}>
                {t("trace")}
              </button>
            )}
          </header>
        )}
        {editing ? (
          <div className="edit">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={t("Edit message")}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void save();
                }
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <div className="edit-actions">
              <span className="muted">{t("Escape to cancel · Enter to save")}</span>
              <button onClick={() => setEditing(false)}>{t("Cancel")}</button>
              <button className="primary" onClick={save}>
                {t("Save")}
              </button>
            </div>
          </div>
        ) : typeof taskID === "string" ? (
          <TaskCard id={taskID} />
        ) : m.payload_schema === "slack.blocks/v1" ? (
          <Blocks m={m} />
        ) : (
          <div className="text">
            {m.also_in_channel && onThread && (
              <button className="link broadcast-of" onClick={onThread}>
                {rootText ? t("replied to a thread: {text}", { text: rootText }) : t("replied to a thread")}
              </button>
            )}
            <RichText text={m.text} name={(id) => session.name(id)} me={me} group={(id) => session.groupMention(id)} channel={(id) => session.channelName(id)} />
            {m.edited_at && <span className="edited"> {t("(edited)")}</span>}
            {m.state === "streaming" && <span className="streaming" aria-label={t("Still writing")} />}
            <LinkPreviews text={m.text} self={m.id} />
            <PagePreviews m={m} />
          </div>
        )}
        {(m.files?.length ?? 0) > 0 && <Attachments files={m.files!} at={{ messageID: m.id, channelID: m.channel_id, threadRoot: m.thread_root_id }} sent={{ author: m.author.id, at: m.created_at }} />}
        {(m.reactions?.length ?? 0) > 0 && (
          <div className="reactions">
            {m.reactions!.map((r) => (
              <Reaction key={r.emoji} emoji={r.emoji} count={r.count} who={r.principal_ids} me={me} onClick={() => react(r.emoji)} />
            ))}
            {m.class !== "ephemeral" && (
              <button className="add-reaction" onClick={openPicker} aria-label={t("Add a reaction")} title={t("Add a reaction")}>
                <IconReaction size={15} />
              </button>
            )}
          </div>
        )}
        {onThread && (m.reply_count ?? 0) > 0 && (
          <button className="replies" onClick={onThread}>
            <IconReply size={15} />
            {tn(m.reply_count!, "{n} reply", "{n} replies")}
          </button>
        )}
      </div>
      <div className="actions" role="toolbar" aria-label={t("Message actions")} hidden={m.class === "ephemeral"}>
        {quickReactions.map((e) => (
          <button key={e} onClick={() => react(e)} aria-label={t("React with {emoji}", { emoji: e })} title={t("React with {emoji}", { emoji: glyph(e) })}>
            {glyph(e)}
          </button>
        ))}
        <button onClick={openPicker} aria-label={t("More reactions")} title={t("More reactions")}>
          <IconReaction />
        </button>
        {onThread && (
          <button onClick={onThread} aria-label={t("Reply in thread")} title={t("Reply in thread")}>
            <IconReply />
          </button>
        )}
        {m.class !== "ephemeral" && (
          <>
            <button
              className={saved ? "on" : ""}
              onClick={() => void session.setSaved(m.channel_id, m.id, !saved).catch(fail)}
              aria-label={saved ? t("Remove from saved") : t("Save for later")}
              aria-pressed={saved}
              title={saved ? t("Remove from saved") : t("Save for later")}
            >
              <IconBookmark filled={saved} />
            </button>
          </>
        )}
        {m.class !== "ephemeral" && (
          <span className="shortcuts more-menu">
            <button
              onClick={(e) => setMore(more ? null : e.currentTarget.getBoundingClientRect())}
              aria-expanded={!!more}
              aria-haspopup="menu"
              aria-label={t("More actions")}
              title={t("More actions")}
            >
              <IconMore />
            </button>
            {more && (
              <PopMenu
                anchor={more}
                label={t("More actions")}
                onClose={() => setMore(null)}
                items={[
                  { label: t("Share message"), icon: <IconShare size={16} />, run: () => setSharing(true) },
                  { label: t("Copy link"), icon: <IconLink size={16} />, run: () => void copyLink() },
                  ...(!m.thread_root_id
                    ? [{ label: t("Mark unread"), icon: <IconUnread size={16} />, run: () => void session.engine.markUnread(m.channel_id, m.seq).catch(fail) }]
                    : []),
                  ...(m.text ? [{ label: t("Copy text"), icon: <IconCopy size={16} />, run: () => void copy(readable(m.text), t("Text copied")) }] : []),
                  { label: t("Remind me about this"), icon: <IconClock size={16} />, run: () => setReminding(more) },
                  { label: t("New task in thread"), icon: <IconTask size={16} />, run: () => setAsking(true) },
                  {
                    label: pinnedBy ? t("Unpin from channel") : t("Pin to channel"),
                    icon: <IconPin size={16} />,
                    run: () => void session.setPinned(m.channel_id, m.id, !pinnedBy).catch(fail),
                  },
                  ...(mine && !editing ? [{ label: t("Edit message"), icon: <IconEdit size={16} />, run: () => setEditing(true) }] : []),
                  ...(canRecall
                    ? [{ label: t("Recall message"), icon: <IconBack size={16} />, run: () => void session.recall(m).catch(fail) }]
                    : []),
                  ...(mine || admin
                    ? [
                        {
                          label: t("Delete message"),
                          icon: <IconTrash size={16} />,
                          danger: true,
                          divider: true,
                          run: () => {
                            if (window.confirm(t("Delete this message? This cannot be undone.")))
                              session.api.remove(m.channel_id, m.id).catch(fail);
                          },
                        },
                      ]
                    : []),
                ]}
              />
            )}
          </span>
        )}
        {shortcuts.length > 0 && (
          <span className="shortcuts">
            <button onClick={(e) => setMenu(menu ? null : e.currentTarget.getBoundingClientRect())} aria-expanded={!!menu} aria-haspopup="menu" aria-label={t("Shortcuts")} title={t("Shortcuts")}>
              <IconBolt />
            </button>
            {menu && (
              <PopMenu
                anchor={menu}
                label={t("Shortcuts")}
                onClose={() => setMenu(null)}
                items={shortcuts.map((sc) => ({ label: sc.label, title: sc.description, run: () => void runShortcut(sc) }))}
              />
            )}
          </span>
        )}
        {started && <span className="shortcut-started">{started}</span>}
      </div>
      {reminding && <RemindPicker anchor={reminding} message={m} onClose={() => setReminding(null)} done={say} />}
      {sharing && <ShareMessage m={m} onClose={() => setSharing(false)} done={say} />}
      {asking && (
        <Suspense fallback={null}>
          <NewTaskSheet channel={m.channel_id} threadRoot={m.thread_root_id ?? m.id} onClose={() => setAsking(false)} />
        </Suspense>
      )}
      {picking && (
        <EmojiPicker
          anchor={picking}
          onClose={() => setPicking(null)}
          onPick={(name) => {
            const has = m.reactions?.find((r) => r.emoji === name)?.principal_ids.includes(me ?? "");
            if (!has) void react(name);
          }}
        />
      )}
    </article>
  );
}

// Reaction is one emoji under a message, and its count; hovering or
// focusing it shows who reacted with it.
function Reaction({ emoji, count, who, me, onClick }: { emoji: string; count: number; who: string[]; me?: string; onClick: () => void }) {
  const session = useSession();
  const mine = !!me && who.includes(me);
  const shown = 12;
  const names = who.filter((p) => p !== me).map((p) => session.name(p));
  const named = names.slice(0, mine ? shown - 1 : shown);
  if (names.length > named.length) named.push(tn(names.length - named.length, "{n} other", "{n} others"));
  if (mine) named.push(t("you"));
  const said = t("{names} reacted with {emoji}", { names: list(named), emoji: `:${emoji}:` });
  const tip = useTip(
    <>
      <span className="tip-emoji">
        <Emoji name={emoji} />
      </span>
      <span>{said.charAt(0).toUpperCase() + said.slice(1)}</span>
    </>,
  );
  return (
    <button className={mine ? "mine" : ""} aria-pressed={mine} onClick={onClick} {...tip.props}>
      <Emoji name={emoji} /> <span className="count">{count}</span>
      {tip.tip}
    </button>
  );
}
